import React from "react";
import styled from "styled-components";
import { Banner } from "@cinesys/ui/components";

const SIGN_IN_URL = "https://app.iconik.io";

// "unauthenticated" now covers both "never had a session" (initial load, no/invalid launch
// token) and "had one, it died mid-use" (see `useInterfaceAccess`'s `SESSION_EXPIRED_EVENT`
// handling) — same recovery either way (sign back in at iconik), so one message names both
// without guessing which one happened. "not-enabled" is a different situation for the user (a
// working session, just no entitlement) — no session-y language, no sign-in link, points at their
// admin instead since re-signing in wouldn't fix it.
const UNAUTHENTICATED_MESSAGE =
  "Your session has expired or could not be verified.";
const NOT_ENABLED_MESSAGE =
  "This interface isn't enabled for your organization. Contact your admin for access.";

const Center = styled.div`
  min-height: 240px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 24px;
  text-align: center;
`;

const SignInLink = styled.a`
  color: var(--blue300);
  font-size: 15px;
  font-weight: 600;
  text-decoration: underline;

  &:hover {
    color: var(--blue400);
  }
`;

export interface InterfaceAccessNoticeProps {
  reason: "unauthenticated" | "not-enabled";
}

/**
 * The centered, in-shell message for `useInterfaceAccess`'s "denied" state — replaces a screen's
 * real content but leaves the header's placeholder chrome in place (Addison: stop blowing the
 * whole page away for this, the old full-screen-error behavior). Copy is deliberately generic for
 * both reasons — neither one names the interface, same "don't give away what the tool is"
 * reasoning as the placeholders it sits under.
 */
const InterfaceAccessNotice = ({ reason }: InterfaceAccessNoticeProps) => (
  <Center>
    <Banner
      error={
        reason === "unauthenticated"
          ? UNAUTHENTICATED_MESSAGE
          : NOT_ENABLED_MESSAGE
      }
    />
    {reason === "unauthenticated" && (
      <SignInLink href={SIGN_IN_URL}>Sign in at iconik</SignInLink>
    )}
  </Center>
);

export default InterfaceAccessNotice;
